import { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import Dialog from '@/components/ui/Dialog/Dialog'
import { FormItem, Form } from '@/components/ui/Form'
import Button from '@/components/ui/Button/Button'
import { Alert } from '@/components/ui/Alert'
import Spinner from '@/components/ui/Spinner'
import PasswordInput from '@/components/shared/PasswordInput'

import { useMasterPasswordStore } from '@/store/masterPasswordStore'
import {
    initSodium,
    deriveKey,
    verifyPassword,
    decodeSalt,
    decodeVerifier,
    zeroize,
} from '@/services/CryptoService'
import { apiGetMasterPasswordMeta } from '@/services/MasterPasswordService'
import type { MasterPasswordMetadata } from '@/services/CryptoService'

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------

const buildSchema = (t: (key: string) => string) =>
    z.object({
        password: z
            .string()
            .min(1, t('masterPasswordUnlock.validation.passwordRequired')),
    })

type FormValues = {
    password: string
}

// ---------------------------------------------------------------------------
// View states
// ---------------------------------------------------------------------------

type View = 'loading' | 'form' | 'verifying' | 'error'

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MasterPasswordUnlockModal = () => {
    const { t } = useTranslation()

    const unlockModalOpen     = useMasterPasswordStore((s) => s.unlockModalOpen)
    const unlockPendingAction = useMasterPasswordStore((s) => s.unlockPendingAction)
    const closeUnlockModal    = useMasterPasswordStore((s) => s.closeUnlockModal)
    const setMasterKey        = useMasterPasswordStore((s) => s.setMasterKey)

    const [view, setView]           = useState<View>('loading')
    const [metadata, setMetadata]   = useState<MasterPasswordMetadata | null>(null)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [loadError, setLoadError] = useState<string | null>(null)

    // Keep the last-entered password so Retry (on load failure) works without
    // re-showing the form — not used for Retry on wrong password (form stays open).
    const savedPasswordRef = useRef<string | null>(null)

    const {
        handleSubmit,
        control,
        formState: { errors },
        reset,
        setFocus,
    } = useForm<FormValues>({
        defaultValues: { password: '' },
        resolver: zodResolver(buildSchema(t)),
        mode: 'onSubmit',
    })

    // -----------------------------------------------------------------------
    // Metadata fetch
    // -----------------------------------------------------------------------

    const fetchMeta = useCallback(async () => {
        if (view === 'error') {
            setView('loading')
            setLoadError(null)
        }
        try {
            const data = await apiGetMasterPasswordMeta()
            setMetadata(data)
            setView('form')
        } catch {
            setLoadError(t('masterPasswordUnlock.errorLoadFailed'))
            setView('error')
        }
    }, [t, view])

    const fetchMetaRef = useRef(fetchMeta)
    const wasOpenRef = useRef(false)

    useEffect(() => {
        fetchMetaRef.current = fetchMeta
    }, [fetchMeta])

    // Fetch metadata whenever the modal opens.
    useEffect(() => {
        if (!unlockModalOpen) {
            wasOpenRef.current = false
            return
        }
        if (wasOpenRef.current) return

        wasOpenRef.current = true
        void fetchMetaRef.current()
    }, [unlockModalOpen])

    // Auto-focus the password input once the form is ready.
    useEffect(() => {
        if (view === 'form') {
            // Defer so the input is mounted before focus is attempted.
            setTimeout(() => setFocus('password'), 0)
        }
    }, [view, setFocus])

    // -----------------------------------------------------------------------
    // Submit: derive key + verify
    // -----------------------------------------------------------------------

    const onSubmit = async (values: FormValues) => {
        if (!metadata) return

        savedPasswordRef.current = values.password
        setView('verifying')
        setSubmitError(null)

        // Yield so the spinner renders before the blocking Argon2id call.
        await new Promise<void>((resolve) => setTimeout(resolve, 0))

        let derivedKey: Uint8Array | null = null

        try {
            await initSodium()

            const salt            = decodeSalt(metadata.salt)
            const storedVerifier  = decodeVerifier(metadata.password_verifier)

            // Helper: defer a synchronous blocking call so the UI can repaint.
            const defer = <T,>(fn: () => T): Promise<T> =>
                new Promise((resolve, reject) =>
                    setTimeout(() => {
                        try { resolve(fn()) } catch (e) { reject(e) }
                    }, 0),
                )

            const { masterKey } = await defer(() =>
                deriveKey(
                    values.password,
                    salt,
                    metadata.opslimit,
                    metadata.memlimit,
                ),
            )
            derivedKey = masterKey

            const valid = verifyPassword(derivedKey, storedVerifier)

            if (!valid) {
                zeroize(derivedKey)
                derivedKey = null
                setSubmitError(t('masterPasswordUnlock.errorIncorrect'))
                setView('form')
                return
            }

            // Success — transfer key ownership to the store.
            setMasterKey(derivedKey)
            derivedKey = null   // ownership transferred; do not zeroize

            savedPasswordRef.current = null
            reset()
            setSubmitError(null)
            closeUnlockModal()
            unlockPendingAction?.()
        } catch {
            // Ensure no key material lingers on unexpected errors.
            if (derivedKey) {
                zeroize(derivedKey)
                derivedKey = null
            }
            setSubmitError(t('masterPasswordUnlock.errorIncorrect'))
            setView('form')
        }
    }

    // -----------------------------------------------------------------------
    // Cancel / close
    // -----------------------------------------------------------------------

    const handleClose = () => {
        // Block close while Argon2id is running to avoid leaving dangling state.
        if (view === 'verifying') return

        savedPasswordRef.current = null
        reset()
        setSubmitError(null)
        setLoadError(null)
        setMetadata(null)
        setView('loading')
        closeUnlockModal()
    }

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------

    const renderLoading = () => (
        <div
            className="flex justify-center items-center py-10"
            role="status"
            aria-label="Loading security settings"
        >
            <Spinner size={32} />
        </div>
    )

    const renderLoadError = () => (
        <div className="space-y-4 py-2">
            <Alert type="danger" showIcon>
                {loadError}
            </Alert>
            <div className="flex justify-end gap-3">
                <Button
                    type="button"
                    variant="plain"
                    onClick={handleClose}
                >
                    {t('masterPasswordUnlock.cancelButton')}
                </Button>
                <Button
                    type="button"
                    variant="solid"
                    onClick={() => void fetchMeta()}
                >
                    {t('masterPasswordUnlock.retryButton')}
                </Button>
            </div>
        </div>
    )

    const isVerifying = view === 'verifying'

    const renderForm = () => (
        <Form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
                <FormItem
                    label={t('masterPasswordUnlock.passwordLabel')}
                    htmlFor="mpu-password"
                    invalid={Boolean(errors.password)}
                    className="!mb-1"
                >
                    <Controller
                        name="password"
                        control={control}
                        render={({ field }) => (
                            <PasswordInput
                                id="mpu-password"
                                placeholder={t('masterPasswordUnlock.passwordPlaceholder')}
                                autoComplete="current-password"
                                disabled={isVerifying}
                                {...field}
                            />
                        )}
                    />
                </FormItem>
                {errors.password && (
                    <p className="mt-0.5 text-[11px] text-red-500 leading-tight">
                        {errors.password.message}
                    </p>
                )}
            </div>

            {submitError && (
                <Alert type="danger" showIcon>
                    {submitError}
                </Alert>
            )}

            <div className="flex justify-end gap-3 pt-1">
                <Button
                    type="button"
                    variant="plain"
                    onClick={handleClose}
                    disabled={isVerifying}
                >
                    {t('masterPasswordUnlock.cancelButton')}
                </Button>
                <Button
                    type="submit"
                    variant="solid"
                    disabled={isVerifying}
                    loading={isVerifying}
                >
                    {isVerifying
                        ? t('masterPasswordUnlock.submittingButton')
                        : t('masterPasswordUnlock.submitButton')}
                </Button>
            </div>
        </Form>
    )

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Dialog
            isOpen={unlockModalOpen}
            disableOpenAnimation
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc={view === 'form' || view === 'error'}
            onClose={handleClose}
            closable={view === 'form' || view === 'error'}
            width={440}
            aria={{
                labelledby: 'mpu-title',
                describedby: 'mpu-description',
            }}
        >
            <div className="px-1">
                {/* Header */}
                <h2
                    id="mpu-title"
                    className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-0.5"
                >
                    {t('masterPasswordUnlock.title')}
                </h2>
                <p
                    id="mpu-description"
                    className="text-xs text-gray-500 dark:text-gray-400 mb-4"
                >
                    {t('masterPasswordUnlock.subtitle')}
                </p>

                <div className="flex min-h-[176px] flex-col justify-center">
                    {view === 'loading'   && renderLoading()}
                    {view === 'error'     && renderLoadError()}
                    {(view === 'form' || view === 'verifying') && renderForm()}
                </div>
            </div>
        </Dialog>
    )
}

export default MasterPasswordUnlockModal
